import { useCallback } from 'react';
import { useRouter } from 'next/compat/router';

import { mapDispensaryToRemote } from 'src/theme/provider/commerce-components/bridge/bridge-map-dispensary';
import type { CheckoutChromeDataInterface, CheckoutHeaderProps } from './checkout-theme-contract';
import { useStorefrontCheckoutDispensary } from './use-storefront-checkout-dispensary';

type CheckoutChromeContext = {
  location: CheckoutChromeDataInterface['location'];
  storefrontUrl?: string;
};

function getStorefrontUrl(
  storeFrontOrigin: string | undefined,
  storeFrontRoot: string | undefined
): string | undefined {
  if (!storeFrontOrigin || !storeFrontRoot) {
    return undefined;
  }

  try {
    return new URL(storeFrontRoot, `${storeFrontOrigin}/`).toString();
  } catch {
    return undefined;
  }
}

const useCheckoutChromeContext = (): CheckoutChromeContext => {
  const { dispensary } = useStorefrontCheckoutDispensary();
  const remoteLocation = mapDispensaryToRemote(dispensary);
  const storefrontUrl = getStorefrontUrl(remoteLocation?.links.storeFrontOrigin, remoteLocation?.links.storeFrontRoot);

  return {
    location: remoteLocation
      ? {
          id: remoteLocation.id,
          name: remoteLocation.name,
          cname: remoteLocation.cname,
          logoUrl: remoteLocation.images.logo || undefined,
        }
      : undefined,
    storefrontUrl,
  };
};

/**
 * Checkout-specific bridge with a strict runtime allowlist. It intentionally
 * excludes all actions plus cart, customer, order, payment, fulfillment,
 * compliance, pricing, submission, confirmation, auth, loaders, and integration values.
 */
export const useCheckoutBridgeDataInterface = (): CheckoutChromeDataInterface => {
  const { location } = useCheckoutChromeContext();
  return { location };
};

/** Host-computed Back semantics passed directly to CheckoutHeader. */
export const useCheckoutHeaderProps = (): CheckoutHeaderProps => {
  const router = useRouter();
  const { location, storefrontUrl } = useCheckoutChromeContext();

  const onBack = useCallback(() => {
    if (storefrontUrl) {
      void router?.push(storefrontUrl);
    }
  }, [router, storefrontUrl]);

  return {
    backLabel: location ? `Back to ${location.name}` : 'Back',
    onBack,
  };
};
